"use client";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import { alpha, Box, IconButton, Stack } from "@mui/material";
import { useState, type MouseEvent, type ReactNode } from "react";

import { carouselArrowSx } from "./carouselArrowSx";

/**
 * Pure, reusable image carousel for product/itinerary cards.
 *
 * Exemple concret : DMC, itineraries, products cards. Hover reveals
 * prev/next chevrons (always visible on touch); navigation wraps circularly.
 * Active image + next image load eagerly, others lazy. Pass overlays
 * (chips, badges) via `children`.
 *
 * A11y labels default to English; apps localise them by passing `prevLabel`
 * / `nextLabel` (the design-system stays i18n-agnostic).
 */
export function ImageCarousel({
  id,
  imageUrls,
  interactive,
  aspectRatio = "4 / 3",
  prevLabel = "Previous image",
  nextLabel = "Next image",
  children,
}: {
  id: string;
  imageUrls: string[];
  interactive: boolean;
  aspectRatio?: string;
  prevLabel?: string;
  nextLabel?: string;
  children?: ReactNode;
}) {
  const [active, setActive] = useState(0);
  const count = imageUrls.length;
  const hasMultiple = interactive && count > 1;

  const stop = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const go = (delta: number) => (e: MouseEvent) => {
    stop(e);
    setActive((i) => (i + delta + count) % count);
  };

  return (
    <Box
      data-testid={`image-carousel-${id}`}
      data-active-index={active}
      sx={{
        position: "relative",
        aspectRatio,
        height: aspectRatio === "auto" ? "100%" : undefined,
        bgcolor: "grey.200",
        overflow: "hidden",
        "&:hover .image-carousel-arrow": { opacity: 1 },
      }}
    >
      {imageUrls.map((url, i) => {
        const eager = i === active || i === (active + 1) % count;
        return (
          <Box
            key={url}
            component="img"
            src={url}
            alt=""
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: i === active ? 1 : 0,
              transition: "opacity .2s ease",
            }}
          />
        );
      })}

      {hasMultiple && (
        <>
          <IconButton
            className="image-carousel-arrow"
            data-testid={`image-carousel-prev-${id}`}
            aria-label={prevLabel}
            size="small"
            onClick={go(-1)}
            sx={carouselArrowSx("left")}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            className="image-carousel-arrow"
            data-testid={`image-carousel-next-${id}`}
            aria-label={nextLabel}
            size="small"
            onClick={go(1)}
            sx={carouselArrowSx("right")}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              position: "absolute",
              bottom: 8,
              left: 0,
              right: 0,
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            {imageUrls.map((url, i) => (
              <Box
                key={url}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: (theme) =>
                    i === active
                      ? theme.palette.common.white
                      : alpha(theme.palette.common.white, 0.6),
                }}
              />
            ))}
          </Stack>
        </>
      )}

      {children}
    </Box>
  );
}
